<template>
  <section id="contact">
    <div class="reveal">
      <div class="section-label">{{ m.contact.label }}</div>
      <h2>{{ m.contact.titleLine1 }}<br>{{ m.contact.titlePrefix }}<em>{{ m.contact.titleEmphasis }}</em></h2>
      <p class="contact-intro">{{ m.contact.intro }}</p>
      <div class="contact-detail">
        <div class="contact-detail-icon">
          <svg viewBox="0 0 24 24"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
        </div>
        <div>
          <div class="contact-detail-label">{{ m.contact.emailLabel }}</div>
          <div class="contact-detail-val"><span id="email-address"></span></div>
        </div>
      </div>
      <div class="contact-detail">
        <div class="contact-detail-icon">
          <svg viewBox="0 0 24 24"><path d="M3 21h18"/><path d="M5 21V7l8-4v18"/><path d="M19 21V11l-6-4"/><path d="M9 9v.01"/><path d="M9 12v.01"/><path d="M9 15v.01"/><path d="M9 18v.01"/></svg>
        </div>
        <div>
          <div class="contact-detail-label">{{ m.contact.companyLabel }}</div>
          <div class="contact-detail-val contact-company">
            <strong>{{ m.contact.companyName }}</strong>
            <span>{{ m.contact.companyAddress }}</span>
            <span>{{ m.contact.companyReg }}</span>
            <span>{{ m.contact.companyTax }}</span>
          </div>
        </div>
      </div>
    </div>

    <form
      v-if="hasAccepted"
      id="contact-form"
      class="reveal visible"
      style="transition-delay:.1s"
      method="POST"
      @submit.prevent="handleContactSubmit"
    >
      <input type="hidden" name="_subject" :value="m.contact.formSubject">
      <!-- FormSubmit: explicit page URL when Referer is stripped on cross-origin POST -->
      <input type="hidden" name="_url" :value="localeUrl">
      <input type="hidden" name="_captcha" value="false">

      <div class="form-row">
        <input type="text" name="name" :placeholder="m.contact.namePlaceholder" required>
        <input type="text" name="company" :placeholder="m.contact.companyPlaceholder">
      </div>
      <input type="email" name="email" :placeholder="m.contact.emailPlaceholder" required>
      <textarea name="message" :placeholder="m.contact.messagePlaceholder"></textarea>

      <div ref="turnstileContainer" class="cf-turnstile" />

      <button type="submit" class="form-submit">{{ m.contact.submit }}</button>
      <div class="form-msg" id="formMsg">{{ m.contact.success }}</div>
    </form>

    <div v-else class="form-consent-blocked reveal visible" style="transition-delay:.1s">
      <p>{{ m.cookies.contactFormBlocked }}</p>
      <button type="button" class="form-consent-btn" @click="acceptAll">
        {{ m.cookies.acceptAll }}
      </button>
    </div>
  </section>
</template>

<script setup lang="ts">
import { onMounted, ref, nextTick, watch } from 'vue'
import { CONTACT_API_URL, CONTACT_EMAIL } from '~/composables/useI18n'

const { messages: m, localeUrl } = useI18n()
const contactEmail = CONTACT_EMAIL
const { hasAccepted, acceptAll, isCrawler } = useCookieConsent()
const { mountTurnstile, teardownTurnstile } = useTurnstile()
const turnstileSiteKey = useRuntimeConfig().public.turnstile.siteKey
const turnstileContainer = ref<HTMLElement | null>(null)

const showFormMessage = (text: string, color = 'red') => {
  const msgEl = document.getElementById('formMsg')
  if (!msgEl) return
  msgEl.textContent = text
  msgEl.style.display = 'block'
  msgEl.style.color = color
}

const handleContactSubmit = async (e: Event) => {
  const form = e.target as HTMLFormElement
  const tokenInput = form.querySelector('input[name="cf-turnstile-response"]') as HTMLInputElement | null

  if (!tokenInput || !tokenInput.value) {
    showFormMessage(m.value.contact.captchaRequired)
    return
  }

  const formData = new FormData(form)
  try {
    const res = await fetch(CONTACT_API_URL, {
      method: 'POST',
      body: formData
    })

    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      showFormMessage(data.message || m.value.contact.captchaError)
      return
    }

    const data = await res.json()
    if (!data.success) {
      showFormMessage(m.value.contact.captchaFailed)
      return
    }

    const tempForm = document.createElement('form')
    tempForm.action = `https://formsubmit.co/${CONTACT_EMAIL}`
    tempForm.method = 'POST'
    // FormSubmit needs a referer/origin; modern browsers often omit path on
    // cross-origin POST. Explicit _url avoids the "open via web server" error.
    tempForm.referrerPolicy = 'origin'
    tempForm.style.display = 'none'

    for (const [key, value] of formData.entries()) {
      if (key === 'cf-turnstile-response') continue
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = key
      input.value = String(value)
      tempForm.appendChild(input)
    }

    // Prefer values already present in formData; only add reserved fields if missing.
    const ensureHidden = (name: string, value: string) => {
      if (formData.has(name)) return
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = name
      input.value = value
      tempForm.appendChild(input)
    }

    ensureHidden('_subject', m.value.contact.formSubject)
    ensureHidden('_captcha', 'false')
    // Required by FormSubmit when Referer is missing/stripped (not related to HSTS/TLS).
    ensureHidden('_url', typeof window !== 'undefined' ? window.location.href : localeUrl.value)
    ensureHidden('_next', `${localeUrl.value}?emailsuccess=1`)

    document.body.appendChild(tempForm)
    tempForm.submit()
  } catch {
    showFormMessage(m.value.contact.sendError)
  }
}

const mountTurnstileWidget = async () => {
  if (!hasAccepted.value || isCrawler.value) return
  await nextTick()
  if (!turnstileContainer.value) return
  await mountTurnstile(turnstileContainer.value, turnstileSiteKey)
}

watch(hasAccepted, async (accepted) => {
  if (!accepted) {
    teardownTurnstile()
    return
  }

  await nextTick()
  useScrollReveal()
  await mountTurnstileWidget()
}, { flush: 'post' })

watch(turnstileContainer, async (container) => {
  if (!container || !hasAccepted.value) return
  await mountTurnstile(container, turnstileSiteKey)
}, { flush: 'post' })

onMounted(() => {
  const email = contactEmail

  const el = document.getElementById('email-address')
  if (el) {
    el.innerHTML = `<a href="mailto:${email}">${email}</a>`
  }

  const form = document.getElementById('contact-form')
  if (form) {
    form.addEventListener('submit', (event) => {
      const tokenInput = form.querySelector('input[name="cf-turnstile-response"]') as HTMLInputElement | null
      if (!tokenInput || !tokenInput.value) {
        event.preventDefault()
        showFormMessage(m.value.contact.captchaRequired)
      }
    })
  }

  void mountTurnstileWidget()
})
</script>